import React from "react";
import {
  createStyles,
  withStyles,
  WithStyles,
  StyleRules
} from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import SectionTitle from "@components/atoms/SectionTitle";
import MuiSelect from "@components/molecules/MuiSelect";
import { RecordEachUserSupportOnly } from "@components/organisms/mgr/common/record/RecordEachUserSupportOnly";
import { GetFacilityUsersResponse } from "@api/requests/facility/getFacilityUsers";
import { GetOptionalCustomInfoResponse } from "@api/requests/units/getOptionalCustomInfo";
import { ErrorsState } from "@stores/domain/errors/types";
import { FieldItem } from "@interfaces/ui/form";

const styles = (): StyleRules =>
  createStyles({
    paperContainer: {
      padding: "30px 32px 32px 32px",
      margin: 16
    },
    userList: {
      listStyle: "none",
      padding: 0,
      marginTop: 24,
      marginBottom: 0,
      borderTop: "1px solid #e5e5e5",
      borderBottom: "1px solid #e5e5e5"
    },
    unit: {
      marginTop: 24,
      "& :last-child ul": {
        borderBottom: "1px solid #e5e5e5"
      }
    },
    unitName: {
      backgroundColor: "#eceff1",
      padding: "8px 16px",
      fontSize: 16,
      "& + ul": {
        borderTop: "none"
      }
    },
    noUser: {
      color: "rgba(0, 0, 0, 0.38)"
    },
    unitUserList: {
      listStyle: "none",
      padding: 0,
      marginTop: 0,
      marginBottom: 1,
      borderTop: "1px solid #e5e5e5"
    },
    noUnit: {
      padding: "78px 0 46px",
      textAlign: "center",
      borderTop: "1px solid #e5e5e5"
    },
    noUserRecordSection: {
      padding: "80px 0 48px",
      textAlign: "center"
    }
  });

type OwnProps = {
  users: GetFacilityUsersResponse["data"];
  optionalCustomInfo: GetOptionalCustomInfoResponse["data"];
  errorState: ErrorsState["records"]["data"];
  unitsOptions: FieldItem[];
  unitLabel?: string;
};

type Props = OwnProps & WithStyles<typeof styles>;

const RecordEachUserListCore = (props: Props): JSX.Element => {
  const { classes } = props;
  const unitLabel = props.unitLabel || "ユニット";
  // unit設定がない場合、通常のユーザー一覧
  if (Array.isArray(props.optionalCustomInfo)) {
    return (
      <Paper elevation={0} className={classes.paperContainer}>
        <SectionTitle label="利用者ごとの記録" isTitleNoMargin />
        {props.users.length > 0 ? (
          <ul className={classes.userList}>
            {props.users.map((user) => (
              <RecordEachUserSupportOnly
                key={user.uif_id}
                user={user}
                errorState={props.errorState}
              />
            ))}
          </ul>
        ) : (
          <div className={classes.noUserRecordSection}>
            利用者が登録されていません。利用者情報画面で登録後、ご利用ください。
          </div>
        )}
      </Paper>
    );
  }

  const defaultUnitValue = 0;
  const [unitValue, setUnitValue] = React.useState(defaultUnitValue);
  const onChangeUnit = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setUnitValue(+e.target.value);
  };
  const facilityUnits =
    unitValue !== defaultUnitValue
      ? props.optionalCustomInfo.facility_units.filter(
          (v) => v.id === unitValue
        )
      : props.optionalCustomInfo.facility_units.filter((v) => v.id);

  return (
    <Paper elevation={0} className={classes.paperContainer}>
      <SectionTitle label="利用者ごとの記録" isTitleNoMargin />
      <div className={classes.unit}>
        <MuiSelect
          name="unit_select"
          label={`${unitLabel}選択`}
          value={unitValue}
          options={props.unitsOptions}
          onChange={onChangeUnit}
          style={{ width: 520 }}
        />
        {unitValue !== defaultUnitValue &&
        facilityUnits.length === 1 &&
        facilityUnits[0].user_ids.length === 0 ? (
          <div className={classes.noUnit}>
            この{unitLabel}には利用者が登録されていません。
          </div>
        ) : (
          facilityUnits.map((unit) => (
            <div key={unit.id}>
              {unitValue === defaultUnitValue && (
                <Typography color="primary" className={classes.unitName}>
                  {unit.unit_name}
                  {unit.user_ids.length === 0 && (
                    <span className={classes.noUser}>
                      （利用者が登録されていません）
                    </span>
                  )}
                </Typography>
              )}
              <ul className={classes.unitUserList}>
                {unit.user_ids.map((id) => {
                  const user = props.users.find((v) => v.uif_id === id);
                  return (
                    user && (
                      <RecordEachUserSupportOnly
                        key={user.uif_id}
                        user={user}
                        errorState={props.errorState}
                      />
                    )
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </Paper>
  );
};

export const RecordEachUserList = withStyles(styles)(RecordEachUserListCore);
